import { api } from './client';

export interface MySQLServerInfo {
  version: string;
  uptime_seconds: number;
  threads_connected: number;
  total_size_bytes: number;
  database_count: number;
}

export interface MySQLDatabase {
  name: string;
  charset: string;
  collation: string;
  size_bytes: number;
  table_count: number;
}

export const fetchMysqlInfo = (): Promise<MySQLServerInfo> =>
  api.get<MySQLServerInfo>('/mysql/info').then((r) => r.data);

export const fetchMysqlDatabases = (): Promise<{ databases: MySQLDatabase[] }> =>
  api.get<{ databases: MySQLDatabase[] }>('/mysql/databases').then((r) => r.data);

export const fetchMysqlCharsets = (): Promise<{ charsets: Record<string, string[]> }> =>
  api.get<{ charsets: Record<string, string[]> }>('/mysql/charsets').then((r) => r.data);

export interface CreateDatabasePayload {
  name: string;
  charset: string;
  collation: string;
}

export const createDatabase = (data: CreateDatabasePayload): Promise<{ ok: boolean; name: string }> =>
  api.post<{ ok: boolean; name: string }>('/mysql/databases', data).then((r) => r.data);

export const dropDatabase = (name: string, confirm_name: string): Promise<{ ok: boolean }> =>
  api.delete<{ ok: boolean }>(`/mysql/databases/${encodeURIComponent(name)}`, {
    data: { confirm_name },
  }).then((r) => r.data);

export const fetchDumpToken = (name: string): Promise<{ token: string }> =>
  api.post<{ token: string }>(`/mysql/databases/${encodeURIComponent(name)}/dump-token`).then((r) => r.data);

export function triggerDumpDownload(name: string, token: string): void {
  window.location.href = `/api/mysql/databases/${encodeURIComponent(name)}/dump?token=${encodeURIComponent(token)}`;
}

// ── Schema viewer (WP55) ──────────────────────────────────────────────────

export interface TableColumn {
  name: string;
  type: string;
  max_length: number | null;
  nullable: boolean;
  default_value: string | null;
  key: string;
  extra: string;
}

export const fetchTables = (dbName: string): Promise<string[]> =>
  api.get<{ tables: string[] }>(`/mysql/databases/${encodeURIComponent(dbName)}/tables`)
    .then((r) => r.data.tables);

export const fetchTableSchema = (dbName: string, tableName: string): Promise<TableColumn[]> =>
  api.get<{ columns: TableColumn[] }>(
    `/mysql/databases/${encodeURIComponent(dbName)}/tables/${encodeURIComponent(tableName)}/schema`,
  ).then((r) => r.data.columns);

// ── Data browser (WP100) ──────────────────────────────────────────────────

export interface TableInfo {
  rowCount: number;
  engine: string;
  charset: string;
  collation: string;
  sizeBytes: number;
}

export interface TableDataResult {
  rows: Record<string, unknown>[];
  total: number;
  columns: string[];
  primaryKey: string | null;
}

export interface RowsParams {
  page: number;
  pageSize: number;
  sortCol: string;
  sortDir: 'asc' | 'desc';
  search: string;
}

export interface QueryResult {
  rows: Record<string, unknown>[];
  columns: string[];
  rowCount: number;
  affectedRows?: number;
  executionMs: number;
  error?: string;
}

export const fetchTableInfo = (db: string, table: string): Promise<TableInfo> =>
  api.get<TableInfo>(`/mysql/${encodeURIComponent(db)}/${encodeURIComponent(table)}/info`).then((r) => r.data);

export const fetchTableRows = (db: string, table: string, params: RowsParams): Promise<TableDataResult> =>
  api.get<TableDataResult>(`/mysql/${encodeURIComponent(db)}/${encodeURIComponent(table)}/rows`, { params }).then((r) => r.data);

export const insertRow = (db: string, table: string, data: Record<string, unknown>): Promise<{ ok: boolean }> =>
  api.post<{ ok: boolean }>(`/mysql/${encodeURIComponent(db)}/${encodeURIComponent(table)}/rows`, { data }).then((r) => r.data);

export const updateRow = (db: string, table: string, pk: string, data: Record<string, unknown>): Promise<{ ok: boolean }> =>
  api.put<{ ok: boolean }>(`/mysql/${encodeURIComponent(db)}/${encodeURIComponent(table)}/rows/${encodeURIComponent(pk)}`, { data }).then((r) => r.data);

export const deleteRow = (db: string, table: string, pk: string): Promise<{ ok: boolean }> =>
  api.delete<{ ok: boolean }>(`/mysql/${encodeURIComponent(db)}/${encodeURIComponent(table)}/rows/${encodeURIComponent(pk)}`).then((r) => r.data);

export const runQuery = (db: string, sql: string): Promise<QueryResult> =>
  api.post<QueryResult>('/mysql/query', { db, sql }).then((r) => r.data);
